import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

import { UserRoleData } from "@/@types/growthos-api/schema/openapi";
import { Button } from "@/Components/v2/atoms/Button";
import { Dialog } from "@/Components/v2/atoms/Dialog";

import { useUpdateUser } from "../api/updateUser";
import DeleteUser from "../DeleteUser/DeleteUser";

const EditUserModal = (props: {
  handleEditUserModal: (data: { data: UserRoleData | null; newIsEditUserOpen: boolean }) => void;
  editedUserData: { isEditUserOpen: boolean; data: UserRoleData | null };
}) => {
  const { handleEditUserModal, editedUserData } = props;
  const updateUserMutation = useUpdateUser();
  const [selectedRole, setSelectedRole] = useState("");

  const ROLE_OPTIONS = [
    { value: "Admin", label: "Admin" },
    { value: "Analyst", label: "Analyst" },
    { value: "Viewer", label: "Viewer" },
  ];

  const ROLE_DESCRIPTIONS = [
    {
      role: "Admin",
      content: ": Access Review, Scenario Builder, Overview pages, and Team Management.",
    },
    { role: "Analyst", content: ": Access Review, Scenario Builder, Overview pages." },
    { role: "Viewer", content: ": Access to Review pages only." },
  ];

  useEffect(() => {
    // set current role
    if (editedUserData?.isEditUserOpen && editedUserData?.data) {
      setSelectedRole(editedUserData.data.growthosRoleName || "");
    }
  }, [editedUserData.isEditUserOpen, editedUserData?.data]);

  const handleRoleChange = (e: SelectChangeEvent<string>) => {
    setSelectedRole(e.target.value);
  };

  const onSave = () => {
    updateUserMutation.mutate(
      {
        cmsUserId: editedUserData.data?.id ?? "",
        body: {
          role: selectedRole as any,
        },
      },
      {
        onSuccess: () => {
          handleEditUserModal({ data: null, newIsEditUserOpen: false });
        },
      }
    );
  };

  return (
    <Dialog
      open={editedUserData.isEditUserOpen}
      onClose={() => handleEditUserModal({ data: null, newIsEditUserOpen: false })}
      aria-labelledby="edit-user-modal-title"
      aria-describedby="edit-user-modal-description"
      maxWidth="sm"
      fullWidth
    >
      <Box
        sx={{
          p: 3,
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" component="h2">
            Edit User: <strong>{editedUserData.data?.email}</strong>
          </Typography>
        </Box>

        {/* Role Selection */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Roles
          </Typography>

          {ROLE_DESCRIPTIONS.map((item, index) => (
            <Typography key={index} variant="body2" sx={{ mb: 0.5, fontSize: "10px" }}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {item.role}
              </Box>
              {item.content}
            </Typography>
          ))}

          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Select User Role</InputLabel>
            <Select value={selectedRole} onChange={handleRoleChange} label="Select User Role">
              {ROLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() =>
              handleEditUserModal({
                data: null,
                newIsEditUserOpen: false,
              })
            }
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={onSave} disabled={updateUserMutation.isPending}>
            {updateUserMutation.isPending ? "Saving..." : "Save and Close"}
          </Button>
        </Box>

        <Divider sx={{ my: 3 }} />

        <DeleteUser
          cmsUserId={editedUserData.data?.id ?? ""}
          invitationId={editedUserData.data?.growthosInvitationId ?? ""}
          handleEditUserModal={handleEditUserModal}
        />
      </Box>
    </Dialog>
  );
};

export default EditUserModal;
